import { canDelete, canEdit as canEditArea } from '../lib/area';
import { DeleteButton } from '../components/DeleteButton';
import { api } from '../lib/http';
import { useRememberedFilter } from '../lib/remember';
import { getAreaName } from '../lib/area';
import { type Issue, type PdfParseResult, type Lookups, type Me, type OrderListItem } from '@ft/shared';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExportButton } from '../components/ExportButton';
import { SavedViews } from '../components/SavedViews';
import { useNewShortcut } from '../components/CommandPalette';
import { OrderForm, type PdfPrefill } from '../components/OrderForm';
import { OrderInbox } from '../components/OrderInbox';
import { useToast } from '../components/ui';
import { upload } from '../lib/download';
import { Cust, Loading, PmChips, StatusPill, Use, useLoad } from '../components/ui';
import { dfmt, eur } from '../lib/format';
import { useI18n } from '../i18n';
import { StaleBanner } from '../components/Freshness';
import { Rest } from './ProjectPage';
import { canSeePath } from '../lib/area';

export function OrdersPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('orders');
  const { data, error, reload } = useLoad<OrderListItem[]>('/api/orders');
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const { data: issues } = useLoad<Issue[]>(canSeePath(me, '/offene-punkte') ? '/api/issues' : null);
  const ending = useMemo(() => new Set((issues ?? []).filter((i) => i.code === 'endingSoon').map((i) => i.key.slice('ending|'.length))), [issues]);
  const [creating, setCreating] = useState(false);
  const [pdf, setPdf] = useState<PdfPrefill | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const canEdit = canEditArea(me);
  useNewShortcut(useCallback(() => setCreating(true), []), canEdit);
  const [toast, say] = useToast();

  // Standard: nur offene Bestellungen; „alle“ zeigt alles, andere Werte sind ein einzelner Status (z. B. aus Links)
  const f = { q: sp.get('q') ?? '', c: sp.get('kunde') ?? '', p: sp.get('projekt') ?? '', pm: sp.get('pm') ?? '', s: sp.get('status') ?? 'open' };
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const base = useMemo(() => {
    if (!data) return [];
    const q = f.q.toLowerCase();
    return data.filter((o) =>
      (!f.c || o.customer.name === f.c) &&
      (!f.p || o.projects.includes(f.p)) &&
      (!f.pm || (f.pm === 'none' ? !o.pms.length : f.pm === 'me' ? o.pms.some((x) => x.id === me.id) : o.pms.some((x) => x.id === f.pm))) &&
      (!q || [o.orderNumber, o.customer.name, ...o.projects].join(' ').toLowerCase().includes(q)));
  }, [data, f.q, f.c, f.p, f.pm, me.id]);
  const isOpenOrder = (o: OrderListItem) => o.kind === 'order' && o.volumeCents - o.billedCents > 100;
  const SEG: [string, (o: OrderListItem) => boolean][] = [['open', isOpenOrder], ['settled', (o) => o.status === 'settled'], ['warranty', (o) => o.kind === 'warranty'], ['alle', () => true]];
  const segFn = SEG.find(([k]) => k === f.s)?.[1];
  const list = base.filter(segFn ?? ((o) => o.status === f.s));

  const customers = [...new Set(data?.map((o) => o.customer.name) ?? [])].sort();
  const projects = [...new Set(data?.filter((o) => !f.c || o.customer.name === f.c).flatMap((o) => o.projects) ?? [])].sort();
  const pms = [...new Map(data?.flatMap((o) => o.pms).map((p) => [p.id, p]) ?? []).values()];
  const projOf = (o: OrderListItem) => o.projectIds.map((id) => lookups?.projects.find((p) => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p);
  const canDel = (o: OrderListItem) => !!lookups && canDelete(me, projOf(o), o.projectIds.length > 0);
  const anyDel = !!data?.some(canDel);
  async function remove(o: OrderListItem) {
    try { await api('DELETE', `/api/orders/${o.id}?version=${o.version}${o.invoiceCount ? '&detach=1' : ''}`); reload(); say(t('del.done')); } catch (e) { say((e as Error).message); }
  }
  const sum = (k: 'volumeCents' | 'billedCents') => list.reduce((s, o) => s + o[k], 0);
  const filtered = !!(f.q || f.c || f.p || f.pm || (f.s !== 'open'));

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('orders.title')}</h1></div>
        <span className="spacer" />
        <SavedViews />
        <ExportButton />
        {pdfErr && <span className="err small">{pdfErr}</span>}
        {canEdit && (
          <label className={`btn${pdfBusy || !lookups ? ' disabled' : ''}`}>
            {pdfBusy ? t('pdf.reading') : t('pdf.fromPdf')}
            <input type="file" accept="application/pdf,.pdf" hidden disabled={pdfBusy || !lookups} onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setPdfBusy(true); setPdfErr(null);
              try { setPdf({ file, result: await upload<PdfParseResult>('POST', '/api/orders/parse-pdf', file) }); } catch (err) { setPdfErr((err as Error).message); } finally { setPdfBusy(false); }
            }} />
          </label>
        )}
        {canEdit && <button className="btn primary" onClick={() => setCreating(true)} disabled={!lookups}>{t('orders.new')}</button>}
      </div>

      <StaleBanner me={me} keys={['scan']} />
      <OrderInbox me={me} canEdit={canEdit} lookups={lookups} onChanged={reload} onCreated={(id) => { reload(); say(<>{t('order.saved')} · <Link to={`/bestellungen/${id}`}>{t('common.open')}</Link></>); }} say={say} />

      <div className="fbar">
        <div className="fld"><label htmlFor="fq">{t('common.search')}</label><input id="fq" type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('orders.searchPh')} /></div>
        <div className="fld"><label htmlFor="fc">{t('common.customer')}</label><select id="fc" value={f.c} onChange={(e) => set('kunde', e.target.value)}><option value="">{t('common.all')}</option>{customers.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="fld"><label htmlFor="fp">{t('common.project')}</label><select id="fp" value={f.p} onChange={(e) => set('projekt', e.target.value)}><option value="">{t('common.all')}</option>{projects.map((p) => <option key={p}>{p}</option>)}</select></div>
        <div className="fld"><label htmlFor="fpm">{t('common.pm')}</label><select id="fpm" value={f.pm} onChange={(e) => set('pm', e.target.value)}><option value="">{t('common.all')}</option><option value="me">{t('common.myProjects')}</option>{pms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}<option value="none">{t('common.withoutPm')}</option></select></div>
        {filtered && <button className="btn ghost" onClick={() => setSp(new URLSearchParams(), { replace: true })}>{t('common.reset')}</button>}
      </div>

      <div className="chips">
        <div className="seg" role="group" aria-label={t('common.status')}>
          {SEG.map(([k, fn]) => <button key={k} aria-pressed={f.s === k} onClick={() => set('status', k === 'open' ? '' : k)}>{t(`orders.seg.${k as 'open'}`)} <em className="cnt">{base.filter(fn).length}</em></button>)}
        </div>
        {!segFn && <span className="pill info">{t('common.status')}: {t(`status.${f.s as 'settled'}`)} <button className="x" aria-label={t('common.reset')} onClick={() => set('status', '')}>×</button></span>}
      </div>

      {!data ? <Loading error={error} /> : (
        <div className="box tablewrap">
          {!list.length ? <div className="empty">{t('orders.empty')}</div> : (
            <table>
              <thead><tr><th>{t('orders.col.number')}</th><th>{t('common.customer')}</th><th>{t('common.project')}</th><th>{t('common.pm')}</th><th>{t('orders.col.orderedOn')}</th><th>{t('orders.col.lastInvoice')}</th><th className="r">{t('orders.col.volume')}</th><th className="r">{t('orders.col.billed')}</th><th className="r">{t('orders.col.rest')}</th><th>{t('orders.col.usage')}</th><th>{t('common.status')}</th>{anyDel && <th aria-label={t('common.archive')} />}</tr></thead>
              <tbody>
                {list.map((o) => (
                  <tr key={o.id} className="click" tabIndex={0} onClick={() => nav(`/bestellungen/${o.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/bestellungen/${o.id}`); }}>
                    <td>
                      <span className="idlink">{o.orderNumber ?? t('common.noNumber')}</span>{ending.has(o.id) && <span className="pill crit mini">{t('orders.endsSoon')}</span>}
                      {o.parent && <div className="muted small">{t('orders.amendment')} · {o.parent.orderNumber ?? t('common.noNumber')}</div>}
                      {o.amendmentCount > 0 && <div className="muted small">{t('orders.withAmendments', { total: eur(o.groupVolumeCents) })}</div>}
                    </td>
                    <td><Cust name={o.customer.name} /></td>
                    <td>{o.projects[0] ?? <span className="muted">–</span>}{o.projects.length > 1 && <span className="tag">+{o.projects.length - 1}</span>}</td>
                    <td><PmChips pms={o.pms} /></td>
                    <td className="num">{dfmt(o.orderDate)}</td>
                    <td className="num">{dfmt(o.lastInvoiceDate)}</td>
                    <td className="r num">{eur(o.volumeCents)}</td>
                    <td className="r num">{eur(o.billedCents)}</td>
                    <td className="r num"><Rest o={o} t={t} /></td>
                    <td>{o.kind === 'order' ? <Use volume={o.volumeCents} billed={o.billedCents} /> : <span className="muted">–</span>}</td>
                    <td><StatusPill s={o.status} /></td>
                    {anyDel && <td className="delcell">{canDel(o) && <DeleteButton compact what={o.orderNumber ?? ''} onDelete={() => remove(o)} disabled={o.amendmentCount > 0}
                      title={o.amendmentCount > 0 ? t('del.hasAmendments') : undefined}
                      confirmText={o.invoiceCount ? t('del.withInvoices', { n: o.invoiceCount }) : undefined} />}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={6}>{t('orders.count', { n: list.length })}</td><td className="r num">{eur(sum('volumeCents'))}</td><td className="r num">{eur(sum('billedCents'))}</td><td className="r num">{eur(sum('volumeCents') - sum('billedCents'))}</td><td colSpan={anyDel ? 3 : 2} /></tr></tfoot>
            </table>
          )}
        </div>
      )}

      {creating && lookups && <OrderForm order={null} lookups={lookups} onClose={() => setCreating(false)} onSaved={(id) => nav(`/bestellungen/${id}`)} />}
      {pdf && lookups && <OrderForm order={null} lookups={lookups} pdf={pdf} onClose={() => setPdf(null)} onSaved={(id) => nav(`/bestellungen/${id}`)} />}
      {toast}
    </section>
  );
}
